import * as Sentry from "@sentry/react";

import type { WsFeed } from "@storyboard/web-client";
import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { db } from "@/db/db";
import { Tx } from "@/db/drizzleUtils";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import {
  BootstrapEventListType,
  bootstrapItem,
  fetchLatestBootstrapEventDate,
  insertBootstrapEvent,
} from "@/models/BootstrapUtils.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { resetAllFeedPageFlags } from "@/models/repositories/Feed";
import { UserContext } from "@/models/StateProviders/userProvider";
import { ResetCoordinator } from "@/services/ResetCoordinator.ts";
import { consumeUnreadAlsoSetAsReadOnNextBootstrap, initializeUnreadsForAllFeeds } from "./actions/calculateUnreads";
import {
  initializeFeedLatestActivity,
  initializeFromAlias,
  initializeFromBot,
  initializeIsAliasFeed,
} from "./actions/initialFeedLoad";
import { MyAccountContext } from "./StateProviders/myAccountProvider";
import { actions, TelemetryContext } from "./TelemetryProvider";

const waitForWritesBeforeFetchingNextPage = false;

export default function useBootstrap() {
  const { trackAction, setAttribute, finishAction } = useContext(TelemetryContext);
  const params = useParams();
  const [invalidWorkspaceError] = useState<boolean>(false);
  const [bootstrapLoading] = useState<boolean>(false);
  const [allComplete, setAllComplete] = useState<boolean>(false);
  const [localAllComplete, setLocalAllComplete] = useState<boolean>(false);
  const [feedIds] = useState(new Map<string, boolean>());
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const { workspaceMembership: myWorkspaceMembership } = useContext(UserContext);

  const { myAccount } = useContext(MyAccountContext);

  const [startTime] = useState(() => Date.now());

  const recursiveBootstrapCall = async ({
    workspaceId,
    bootstrapEvent,
    page,
    pageSize = 50000,
    accountId,
    slowLoad,
    mine,
    tx,
    bootstrapItemsSize,
    timestamp,
  }: {
    workspaceId: string;
    bootstrapEvent: BootstrapEventListType;
    page: number;
    pageSize: number;
    accountId: string;
    slowLoad: boolean;
    mine?: boolean;
    tx?: Tx;
    bootstrapItemsSize?: number;
    timestamp?: string;
  }) => {
    const networkSpan = trackAction(actions.bootstrapNetwork(bootstrapEvent.bootstrapFunctionName, page));
    networkSpan.setAttribute("bootstrap.function", bootstrapEvent.bootstrapFunctionName);
    networkSpan.setAttribute("bootstrap.page", page);
    networkSpan.setAttribute("bootstrap.pageSize", pageSize);

    try {
      const payload = timestamp
        ? {
            bootstrapEvent,
            workspaceId,
            page,
            pageSize,
            mine,
            timestamp,
          }
        : {
            bootstrapEvent,
            workspaceId,
            page,
            pageSize,
            mine,
          };

      const responseData = await bootstrapItem(payload);
      await insertBootstrapEvent("bootstrap", workspaceId);

      if (bootstrapEvent.bootstrapFunctionName === "bootstrapWorkspaceFeedPermissions") {
        log.info("bootstrapWorkspaceFeedPermissions", responseData, {
          workspaceId,
          page,
          pageSize,
          mine,
        });
      }

      networkSpan.setAttribute("bootstrap.responseSize", responseData.length);
      finishAction(actions.bootstrapNetwork(bootstrapEvent.bootstrapFunctionName, page));

      if (bootstrapEvent.feed) {
        const feeds: WsFeed[] = responseData as WsFeed[];
        for (const feed of feeds) {
          feedIds.set(feed.id, true);
        }
      }

      if (responseData?.length > 0) {
        const subSpan = trackAction(actions.bootstrapDbWrite(bootstrapEvent.bootstrapFunctionName, page));

        subSpan.setAttribute("bootstrap.function", bootstrapEvent.bootstrapFunctionName);
        subSpan.setAttribute("bootstrap.page", page);
        subSpan.setAttribute("bootstrap.pageSize", pageSize);
        subSpan.setAttribute("bootstrap.responseSize", responseData.length);

        await bootstrapEvent.createManyFunctionName(responseData);
        insertBootstrapEvent(bootstrapEvent.bootstrapFunctionCallResponseName, bootstrapEvent.tableName).then(() => {});
        finishAction(actions.bootstrapDbWrite(bootstrapEvent.bootstrapFunctionName, page));
      } else {
        log.info(
          "No data found for bootstrap event",
          bootstrapEvent.bootstrapFunctionName,
          bootstrapEvent.bootstrapFunctionCallResponseName,
        );
      }

      if (responseData?.length >= pageSize && bootstrapEvent.recursive !== false) {
        return await recursiveBootstrapCall({
          workspaceId,
          bootstrapEvent,
          page: page + 1,
          pageSize,
          accountId,
          tx,
          bootstrapItemsSize,
          slowLoad,
          timestamp,
        });
      }
      return responseData?.length ?? 0;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const updateLocalFeed = async () => {
    trackAction(actions.bootstrapAfterHooksInitialUnreadItems());
    setAttribute("accountId", myAccount?.id);

    const feedKeys = Array.from(feedIds.keys());

    if (!myWorkspaceMembership.accountId) {
      throw new Error("Account ID is required");
    }

    const alsoSetAsRead = consumeUnreadAlsoSetAsReadOnNextBootstrap();
    await db.$client.transaction(async (tx) => {
      await initializeIsAliasFeed(tx);
      await initializeFromAlias(tx);
      await initializeFromBot(tx);
      await initializeFeedLatestActivity(tx);
      await initializeUnreadsForAllFeeds({
        membership: {
          ...myWorkspaceMembership,
          accountId: myWorkspaceMembership.accountId ?? "",
        },
        alsoSetAsRead,
        tx,
      });
    });

    emitTableUpdates(["feed", "item"]);

    const promises = [];
    for (const id of feedKeys) {
      // if you are on a feed, load that data in the bootstrap
      if (params?.feedId && params?.workspaceId && id === params?.feedId) {
        // skip for now
        // promises.push(fetchFeedItemsAndDownloadEvents(id, activeWorkspaceId));
      }
      feedIds.delete(id);
    }
    await Promise.all(promises);
    // This can get done in parallel
    finishAction(actions.bootstrapAfterHooksInitialUnreadItems());
  };

  const bootstrapTransactions = async ({
    workspaceId,
    bootstrapItems,
    slowLoad,
  }: {
    workspaceId: string;
    bootstrapItems: BootstrapEventListType[];
    slowLoad: boolean;
  }) => {
    const bootstrapItemsWithTimestamp = await Promise.all(
      bootstrapItems?.map(async (item) => {
        const hasPreviousTimestamp = await fetchLatestBootstrapEventDate(item.bootstrapFunctionCallResponseName);
        const timestamp = hasPreviousTimestamp ? new Date(hasPreviousTimestamp).toISOString() : null;
        return {
          ...item,
          timestamp,
        };
      }),
    );

    const bootstrapPromises = [];
    await db.$client.transaction(async (tx) => {
      if (bootstrapItemsWithTimestamp?.length > 0) {
        for (const bootstrapEvent of bootstrapItemsWithTimestamp) {
          const payload = {
            workspaceId,
            bootstrapEvent,
            page: 0,
            pageSize: bootstrapEvent.pageSize,
            accountId: myAccount?.id,
            mine: bootstrapEvent.mine,
            tx,
            bootstrapItemsSize: bootstrapItems.length,
            timestamp: bootstrapEvent.timestamp || undefined,
            slowLoad,
          };
          bootstrapPromises.push(recursiveBootstrapCall(payload));
        }
      }
    });

    return await Promise.all(bootstrapPromises)
      .then((data) => {
        console.debug(bootstrapPromises?.length);
        console.debug({ data });
        return bootstrapPromises?.length;
      })
      .catch((data) => {
        console.error({ data });
      });
  };

  const bootstrapPaginatedApplication = async ({
    bootstrapItems,
    workspaceId,
    forceBootstrap,
  }: {
    bootstrapItems: BootstrapEventListType[];
    workspaceId?: string;
    forceBootstrap?: boolean;
  }) => {
    if (bootstrapLoading && !forceBootstrap) {
      return;
    }
    setAllComplete(false);
    setLocalAllComplete(false);
    feedIds.clear();
    setActiveWorkspaceId(() => workspaceId);
    setAttribute("accountId", myAccount?.id);
    setAttribute("workspaceId", workspaceId);
    setAttribute("bootstrap.awaited", waitForWritesBeforeFetchingNextPage ? "true" : "false");
    trackAction(actions.bootstrapFull());
    trackAction(actions.bootstrapLoad());

    // Reset feed page flags so warmup will re-fetch on next load
    await resetAllFeedPageFlags();

    const registry = BootstrapEventsRegistry.getInstance();
    const hasExistingBootstrapEvent = await registry.hasBootstrapForWorkspace(workspaceId ?? undefined);
    const slowLoad = !hasExistingBootstrapEvent;

    Sentry.setContext("bootstrap", {
      mode: slowLoad ? "full" : "incremental",
      workspace_id: workspaceId,
      bootstrap_items: bootstrapItems.length,
    });

    if (slowLoad) {
      console.log(`[Bootstrap] Start full ➞ ${workspaceId}`);
      await bootstrapTransactions({
        workspaceId,
        bootstrapItems,
        slowLoad,
      });
      setLocalAllComplete(() => true);
      console.log(`[Bootstrap] Finish full ➞ ${workspaceId}`);
      finishAction(actions.bootstrapFull());
      return;
    }

    console.log(`[Bootstrap] Start incremental ➞ ${workspaceId}`);
    const finish = await bootstrapTransactions({
      workspaceId,
      bootstrapItems,
      slowLoad,
    });
    console.log(`[Bootstrap] Finish incremental ➞ ${workspaceId} ➞ ${finish}`);
    await updateLocalFeed();
    setLocalAllComplete(() => true);
    finishAction(actions.bootstrapFull());
  };

  useEffect(() => {
    if (feedIds.size > 0 && localAllComplete && myAccount) {
      updateLocalFeed()
        .then(() => {
          finishAction(actions.bootstrapLoad());
          log.info("DONE: bootstrap complete", Date.now() - startTime);
          setAllComplete(true);
          ResetCoordinator.getInstance()
            .start()
            .then(() => {});
        })
        .catch((e) => {
          finishAction(actions.bootstrapLoad(), { error: e.message });
          finishAction(actions.bootstrapFull(), { error: e.message });
          log.error("Error in updateLocalFeed", e);
        });
    } else if (feedIds?.size === 0 && localAllComplete && myAccount) {
      finishAction(actions.bootstrapLoad());
      log.info("DONE: bootstrap complete (no feeds)", Date.now() - startTime);
      setAllComplete(true);
      ResetCoordinator.getInstance()
        .start()
        .then(() => {});
    }
  }, [feedIds, params, localAllComplete, myAccount]);

  return {
    invalidWorkspaceError,
    allComplete,
    bootstrapPaginatedApplication,
    activeWorkspaceId,
  };
}
